<template>
  <v-container fluid class="px-12 py-4" v-if="isLoaded">
    <v-row>
      <v-col cols="8">
        <breadcrumbs></breadcrumbs>
        <h1 data-cy="locationFormTitle">Location Form</h1>
      </v-col>
    </v-row>
    <p class="mb-0" v-if="record.locationId" data-cy="created-tag">
      Created on {{ formatTimestamp(record.created) }} by
      {{ creationUserEmail }}
    </p>
    <v-divider class="mb-4"></v-divider>
    <v-alert v-if="structureIsLocked" type="info">
      The Organization Structure is locked. The form has been disabled.
    </v-alert>
    <v-alert v-if="buildIsClosed" type="info">
      The build is closed. The form has been disabled.
    </v-alert>
    <v-form
      @submit.prevent="onSubmit"
      class="mt-10"
      ref="locationForm"
      v-model="valid"
    >
      <div data-cy="locationFormInputNameContainer">
        <label class="och-label">Location Name</label>
        <v-text-field
          :disabled="isDisabled"
          :rules="requiredRule"
          data-cy="locationFormInputName"
          outlined
          v-model="record.name"
        ></v-text-field>
      </div>

      <div v-if="isOchinUser">
        <label class="och-label">Abbreviation</label>
        <unique-text-field
          :disabled="isDisabled"
          :entityIdValue="locationId"
          :list="locationList"
          attributeName="abbreviation"
          entityName="location"
          outlined
          required
          v-model="record.abbreviation"
        ></unique-text-field>
      </div>

      <div v-if="isOchinUser" data-cy="locationFormInputNumberContainer">
        <label class="och-label">Location Number</label>
        <div class="caption font-weight-light">
          Should be a unique number comprised of the SA # + Location #
        </div>
        <unique-text-field
          :disabled="isDisabled"
          :entityIdValue="locationId"
          :list="locationList"
          :rules="numberRule"
          attributeName="locationNumber"
          entityName="location"
          v-model="record.locationNumber"
        ></unique-text-field>
      </div>

      <label class="och-label">Address</label>
      <div class="d-flex align-baseline" data-cy="selectAddress">
        <v-select
          :disabled="isDisabled"
          :items="addressList"
          :menu-props="{ closeDelay: 50000 }"
          :rules="requiredRule"
          data-cy="menuAddressList"
          item-value="addressId"
          outlined
          v-model="record.addressId"
        >
          <template v-slot:selection="data">
            <span>{{ data.item.address }},&nbsp;</span>
            <span v-if="data.item.address2"
              >{{ data.item.address2 }},&nbsp;</span
            >
            <span>{{ data.item.city }},&nbsp;</span>
            <span>{{ data.item.state }}&nbsp;</span>
            <span>{{ data.item.zip }}</span>
          </template>
          <template v-slot:item="data">
            <div :data-cy="`addressOption${data.item.addressId}`">
              <span>{{ data.item.address }},&nbsp;</span>
              <span v-if="data.item.address2"
                >{{ data.item.address2 }},&nbsp;</span
              >
              <span>{{ data.item.city }},&nbsp;</span>
              <span>{{ data.item.state }}&nbsp;</span>
              <span>{{ data.item.zip }}</span>
            </div>
            <v-spacer></v-spacer>
          </template>
        </v-select>
        <address-dialog
          :disabled="isDisabled"
          class="ml-1"
          data-cy="addressDialogAdd"
          mode="create"
          v-model="record.addressId"
        ></address-dialog>
        <address-dialog
          :disabled="isDisabled"
          class="ml-1"
          data-cy="addressDialogEdit"
          mode="edit"
          v-if="record.addressId"
          v-model="record.addressId"
        ></address-dialog>
      </div>

      <label class="och-label">Area Code</label>
      <v-text-field
        :disabled="isDisabled"
        :counter="3"
        :rules="[areaCodeRule]"
        data-cy="locationAreaCode"
        outlined
        v-model="record.locationAreaCode"
      ></v-text-field>
      <label class="och-label">Phone (include area code)</label>
      <v-text-field
        :disabled="isDisabled"
        data-cy="locationPhone"
        outlined
        v-model="record.locationPhone"
      ></v-text-field>

      <label class="och-label">Appointment Phone (include area code)</label>
      <v-text-field
        :disabled="isDisabled"
        data-cy="appointmentPhone"
        outlined
        v-model="record.appointmentPhone"
      ></v-text-field>

      <label class="och-label">Fax (include area code)</label>
      <v-text-field
        :disabled="isDisabled"
        data-cy="locationFax"
        outlined
        v-model="record.locationFax"
      ></v-text-field>

      <div class="text-right">
        <v-btn outlined @click="cancel" color="grey darken-2" class="mr-3"
          >Cancel</v-btn
        >
        <v-btn
          :disabled="!valid || isDisabled"
          @click="save"
          color="primary"
          data-cy="locationFormSave"
          outlined
          >Save</v-btn
        >
      </div>
    </v-form>
  </v-container>
</template>

<script>
import { mapValues } from "lodash";
import { formValueShouldBeNull } from "@/lib/componentUtil";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";

import AddressDialog from "@/components/static/AddressDialog";
import Breadcrumbs from "@/views/Breadcrumbs";
import UniqueTextField from "@/components/static/UniqueTextField";
import { formatTimestamp } from "@/lib/componentUtil";

export default {
  components: {
    AddressDialog,
    Breadcrumbs,
    UniqueTextField,
  },
  computed: {
    creationUserEmail() {
      const { record, getUserById } = this;
      if (record.locationId) {
        return (getUserById(record.createdBy) || { email: "(Not Saved)" })
          .email;
      }
      return "";
    },
    isCreateForm() {
      return !this.locationId;
    },
    isROI() {
      return (
        (!!this.record.name &&
          !!this.record.name
            .split(/[^a-zA-z]/)
            .find(v => v.toLowerCase() === "roi")) ||
        !!this.record.name
          .split(/[^a-zA-z]/)
          .find(v => v.toLowerCase() === "him")
      );
    },
    buildIsClosed() {
      return this.organizationStatusIsGreaterThanOrEqualTo("buildClosed");
    },
    isDisabled() {
      if (this.buildIsClosed) {
        return this.buildIsClosed;
      }

      return this.structureIsLocked;
    },
    locationId() {
      return parseInt(this.$route.params.locationId, 10);
    },
    numberRule() {
      return [
        v =>
          !v ||
          !!this.organization.serviceAreaId ||
          "This field requires that there is a Service Area # assigned to the organization first. Come back to this field once a Service Area # has been assigned.",
        v =>
          !v ||
          (v && v.startsWith(`${this.organization.serviceAreaId}`)) ||
          `Must start with ${this.organization.serviceAreaId} (Service Area #)`,
        v =>
          !v ||
          (v && v.length === this.requiredLength) ||
          `Must have a length of ${this.requiredLength} digits`,
        v =>
          !v ||
          (v && /^[0-9]+$/.test(v)) ||
          "Location number must be only contain numbers",
      ];
    },
    redirect() {
      return this.$route.query.redirect;
    },
    requiredLength() {
      if (this.isROI) {
        return 14;
      }
      const serviceAreaIdLength = `${this.organization.serviceAreaId}`.length;
      return serviceAreaIdLength + 3;
    },
    structureIsLocked() {
      return this.getStructureStatus;
    },
    ...mapGetters({
      getStructureStatus: "organizationStatus/getStructureStatus",
      getUserById: "user/getById",
      organizationStatusIsGreaterThanOrEqualTo:
        "organizationStatus/organizationStatusIsGreaterThanOrEqualTo",
    }),
    ...mapState({
      addressList: state => state.address.list,
      addressRecord: state => state.address.record,
      isOchinUser: state => state.session.isOchinUser,
      departmentList: state => state.department.list,
      locationList: state => state.location.list,
      organization: state => state.organization.record,
      organizationId: state => state.session.user.organizationId,
      record: state => state.location.record,
    }),
  },
  async created() {
    this.setLocationRecord({});

    if (!this.isCreateForm) {
      try {
        await this.getLocation(this.locationId);
      } catch (e) {
        console.error(e);
        return this.$notifyError({
          message: "Unable to fetch location record.",
        });
      }
    }

    await Promise.all([
      this.getDepartmentList({ reload: false }),
      this.getLocationList({ reload: false }),
      this.getAddressList({ reload: false }),
      this.getUserList({ reload: false }),
      this.getOrganization(this.organizationId),
    ]);

    if (this.record.addressId) {
      try {
        await this.getAddress(this.record.addressId);
      } catch (e) {
        console.error(e);
        return this.notifyError({ message: "Unable to fetch address record." });
      }
    }
    this.isLoaded = true;
  },
  data() {
    return {
      areaCodeRule: v => {
        if (typeof v !== "string") {
          return true;
        }

        // Hide validation if user backspaces to clear values.
        if (!v.trim()) {
          return true;
        }

        if (!/^[0-9]+$/.test(v)) {
          return "Area Code must only contain numbers.";
        }

        if (!!v && v.trim().length === 3) {
          return true;
        }
        return "Area Code must be 3 digits long.";
      },
      isLoaded: false,
      requiredRule: [v => !!v || "Required field"],
      valid: false,
    };
  },
  methods: {
    formatTimestamp,
    ...mapActions({
      createLocation: "location/create",
      getAddress: "address/get",
      getAddressList: "address/list",
      getDepartmentList: "department/list",
      getLocation: "location/get",
      getLocationList: "location/list",
      getUserList: "user/list",
      getOrganization: "organization/getByOrganizationId",
      updateLocation: "location/update",
    }),
    ...mapMutations({
      setLocationRecord: "location/setRecord",
    }),
    formatRecord(record) {
      const recordFormatted = mapValues(record, value => {
        if (formValueShouldBeNull(value)) {
          return null;
        }
        return value;
      });

      if (recordFormatted.abbreviation) {
        recordFormatted.abbreviation =
          recordFormatted.abbreviation.toUpperCase();
      }

      return recordFormatted;
    },
    cancel() {
      this.$router.push({ name: this.redirect || "structureList" });
    },
    async save() {
      const recordFormatted = this.formatRecord(this.record);
      const serverAction = this.isCreateForm ? "created" : "updated";

      try {
        if (!this.isCreateForm) {
          await this.updateLocation([this.locationId, recordFormatted]);
          this.$notifySuccess({
            message: "Location has been successfully updated.",
          });
        } else {
          recordFormatted.organizationId = this.organizationId;
          await this.createLocation(recordFormatted);
          this.$notifySuccess({
            message: `Location has been successfully ${serverAction}.`,
          });
        }
      } catch (e) {
        console.error(e);
        return this.$notifyError({
          message: "Could not save location.",
        });
      }

      this.$router.push({ name: this.redirect || "structureList" });
    },
  },
  watch: {
    "record.name": {
      handler() {
        if (this.isLoaded) {
          this.$refs.locationForm.validate();
        }
      },
    },
  },
};
</script>
